import { Link, Stack, Typography } from '@mui/material';

import { useSpotterModeChangeStatus } from '@/api/device/useSpotterModeChangeStatus';
import { useDevice, useDeviceSubscriptionStatus } from '@/api/devices';
import { SpotterOtaInProgressAlert } from '@/components/spotter/SpotterOtaInProgressAlert';
import { CellularDataSection } from '@/components/spotter/configurationTab/CellularDataSection';
import { NoActiveTelemetryAlert } from '@/components/spotter/configurationTab/NoActiveTelemetryAlert';
import {
    CellularModeChangeText,
    PendingModeChangeAlert,
    SatelliteModeChangeText,
} from '@/components/spotter/configurationTab/PendingModeChangeAlert';
import { SatelliteDataSection } from '@/components/spotter/configurationTab/SatelliteDataSection';
import { useActiveOtaPlan } from '@/components/spotter/useActiveOtaPlan';
import { InlineAlertBody } from '@/components/ui/InlineAlert';

export const DataTransmissionSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { data: device } = useDevice();
    const spotterId = device?.spotterId;
    const { data: subscriptionStatus } = useDeviceSubscriptionStatus({ spotterId });
    const { pendingModeSettingGroup, staleChangeRequest } = useSpotterModeChangeStatus({ spotterId });
    const activeOtaPlan = useActiveOtaPlan();
    const isOtaInProgress = !!activeOtaPlan;

    const isTelemetryDisabled =
        subscriptionStatus && !subscriptionStatus?.cellEnabled && !subscriptionStatus?.satEnabled;
    const isLocked = readOnly || isOtaInProgress;

    return (
        <Stack gap={2}>
            <Stack gap={1}>
                <Typography variant="h4">Data transmission</Typography>
                <Typography>
                    Configure how your Spotter collects and transmits data. View our{' '}
                    <Link
                        href="https://sofarocean.notion.site/Spotter-Data-Modes-e78a76f5f94e49f58bfd68845113c4a7"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Spotter Data Modes documentation
                    </Link>{' '}
                    for more details.
                </Typography>
            </Stack>
            {!readOnly && isTelemetryDisabled && <NoActiveTelemetryAlert spotterId={spotterId} />}
            <SpotterOtaInProgressAlert>
                <InlineAlertBody testid="ota-in-progress-alert">
                    You will not be able to make any changes to your system&apos;s mode configurations until this update
                    process is complete.
                </InlineAlertBody>
            </SpotterOtaInProgressAlert>
            {!isTelemetryDisabled && pendingModeSettingGroup && (
                <PendingModeChangeAlert
                    readOnly={readOnly}
                    staleChangeRequest={staleChangeRequest}
                    targetModeText={
                        <>
                            {pendingModeSettingGroup === 'cellular' && (
                                <CellularModeChangeText
                                    currentSettings={device?.cellularData?.currentCellularFirmwareSettings}
                                    targetSettings={device?.cellularData?.targetCellularFirmwareSettings}
                                />
                            )}
                            {pendingModeSettingGroup === 'satellite' && (
                                <SatelliteModeChangeText
                                    targetMode={device?.targetMode}
                                    targetMessagePeriod={device?.targetMessagePeriod}
                                />
                            )}
                        </>
                    }
                />
            )}
            <SatelliteDataSection readOnly={isLocked} />
            <CellularDataSection readOnly={isLocked} />
        </Stack>
    );
};
